<script setup>
  import {ref, onMounted, watch, computed, getCurrentInstance, toRaw, onBeforeUnmount, inject, defineExpose, defineEmits, defineProps, nextTick } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import { storeToRefs } from 'pinia'
  import { usePageLoadingStore } from '../../../../../../stores/pageLoading'
  import { useUserStore } from '../../../../stores/user'
  import JobFairJobNoticeDetailModal from '../../../../components/modal/jobFair/JobFairJobNoticeDetailModal.vue'
  import JobFairCorpApplicantResumeSelectModal from '../../../../components/modal/jobFair/JobFairCorpApplicantResumeSelectModal.vue'
  import JobFairResumeDetailModal from '../../../../components/modal/jobFair/JobFairResumeDetailModal.vue'
  import JobFairCorpApplicantResumeUploadFileModal from '../../../../components/modal/jobFair/JobFairCorpApplicantResumeUploadFileModal.vue'
  import JobFairApplyResumeConfirmModal from "@/pages/user/components/modal/jobFair/JobFairApplyResumeConfirmModal.vue";
  import mypageJobFairJobNoticeService from '../../../../services/mypage/jobFair/mypageJobFairJobNoticeService'
  import mypageJobFairService from '../../../../services/mypage/jobFair/mypageJobFairService'
  import JobFairCorpInfoDetailModal from '../../../../components/modal/jobFair/JobFairCorpInfoDetailModal.vue'
  import mypageJobFairApplicantService from '../../../../services/mypage/jobFair/mypageJobFairApplicantService'
  import mypageJobFairResumeService from "../../../../services/mypage/jobFair/mypageJobFairResumeService";

  const route = useRoute()
  const router = useRouter()
  const loadingStore = usePageLoadingStore()

  const { user, isAuthenticated } = storeToRefs(useUserStore());
  const globals = getCurrentInstance().appContext.config.globalProperties;
  const { $pageListBoardNo, alert, confirm } = globals;

  const props = defineProps({
    corpId: {
      type: [String, Number],
      default: null,
    }
  });

  const currentPage = ref(1);
  const pageSize = ref(10);
  const totalPages = ref(1);
  const totalCount = ref(0);
  const list = ref([]); // 관심 기업 리스트
  const corpDetail = ref({});

  const corpInfoModal = ref(null);
  const jobNoticeModal = ref(null); // 기업정보
  const resumeSelectModal = ref(null); // 이력서 보기
  const resumeDetailModal = ref(null); // 이력서 상세 보기
  const applyResumeConfirmModal = ref(null); // 회사 이력서 안내
  const resumeUploadFileModal = ref(null); // 회사 이력서 업로드

  const isResumeSelectModalOpen = ref(false);
  const isResumeDetailModalOpen = ref(false);
  const isApplyResumeConfirmModalOpen = ref(false);
  const isResumeUploadFileModalOpen = ref(false);

  const pageSizeGroup = ref([
    { label:"10개씩 보기", value: 10 },
    { label:"30개씩 보기", value: 30 },
    { label:"50개씩 보기", value: 50 },
    { label:"100개씩 보기", value: 100 },
  ])


  const onSelectPage = (pageNo) => {
    currentPage.value = pageNo;
    retrieveList();
  }

  // 페이지당 개수 변경
  const onSelectPageSize = (option) => {
    pageSize.value = option.value;
    currentPage.value = 1;
    retrieveList();
  }

  const retrieveCorpDetail = (corpId) => {
    loadingStore.loading = true;
    mypageJobFairService.retrieveCorporation(corpId)
      .then( (res) => {
        if(res.data.code.toLowerCase() === 'success') {
          const result = res.data.result;
          corpDetail.value = result;
        }
        loadingStore.loading = false;
      })
      .catch( (error) =>  {
        loadingStore.loading = false;
      });
  }

  const retrieveList = (newCorpId) => {
    let paramObj = {
      corpId: newCorpId || props.corpId,
      pageNumber: currentPage.value || 0,
      size: pageSize.value || 10,
    }

    loadingStore.loading = true;
    mypageJobFairJobNoticeService.retrieveUserJobNoticeListPaging(paramObj)
      .then( (res) => {
        if(res.data.code.toLowerCase() === 'success') {
          const result = res.data.result;
          // console.log(result);
          totalPages.value = result.totalPages;
          totalCount.value = result.totalElements;
          list.value = $pageListBoardNo(result.content, totalCount.value, pageSize.value, currentPage.value);
        }
        loadingStore.loading = false;
      })
      .catch( (error) =>  {
        loadingStore.loading = false;
      });
  }

  // 기업정보 보기
  const onClickCorp = async () => {
    const result = await corpInfoModal.value.show({
      corpId: corpDetail.value.corpId,
      interestYn: corpDetail.value.interestYn,
    });
  }

  // 채용공고 보기
  const onClickJobNotice = async (jobNotice) => {
    // console.log(item);
    await jobNoticeModal.value.show({
      jobNoticeSeq: jobNotice.jobNoticeSeq,
      corpId: jobNotice.corpId,
    });

    retrieveList();
  }

  // 입사지원 - 협회 이력서
  const onClickCorpApplicant = async (jobNotice) => {
    const paramObj = {
      jobNoticeSeq: jobNotice.jobNoticeSeq,
      userId: user.value.userId,
    };

    // 지원여부 확인
    mypageJobFairApplicantService.retrieveApplicantByUserId(paramObj)
      .then( async (res) => {
        loadingStore.loading = false;
        if(res.data.code.toLowerCase() === 'success') {
          const result = res.data.result;
          if(result.length > 0) {
            alert(["이미 '입사지원'이 완료된 '채용공고'입니다."]);
            return;
          }

          if(jobNotice.fileYn !== 'Y') { // 협회이력서
            await onClickApplyResume(jobNotice);
          } else { // 회사이력서
            isApplyResumeConfirmModalOpen.value = true;
            await nextTick();
            const ok = await applyResumeConfirmModal.value.show();
            if(ok) {
              isResumeUploadFileModalOpen.value = true;
              await nextTick();
              const result = await resumeUploadFileModal.value.show();
              if(result) {
                mypageJobFairResumeService.createResumeFile(result)
                  .then((res) => {
                    if(res.data.code.toLowerCase() === 'success') {
                      const paramObj = {
                        jobNoticeSeq: jobNotice.jobNoticeSeq,
                        resumeId: res.data.result,
                      };
                      createApplicant(paramObj).then((isSuccess) => {
                        if (isSuccess) {
                          retrieveList();
                        }
                      });
                    } else {
                      alert(["입사지원을 실패하였습니다."]);
                    }
                  })
                  .catch( (res) =>  {
                    alert(["입사지원을 실패하였습니다."]);
                  });
              }
            }
          }
        }
      })
      .catch( (res) =>  {
        loadingStore.loading = false;
      });
  }

  // 이력서 상세 보기
  const onShowResumeDetail = async (resumeId) => {
    isResumeDetailModalOpen.value = true;
    await nextTick();
    await resumeDetailModal.value.show({
      resumeId,
      interestYn: 'N',
      showDeleteBtn: false,
      from: 'JobFairCorpApplicantResumeSelectModal',
    });
  }
    
  // 입사지원
  const onClickApplyResume = async (jobNotice) => {
    isResumeSelectModalOpen.value = true;
    await nextTick();
    const result = await resumeSelectModal.value.show({
      jobNoticeSeq: jobNotice.jobNoticeSeq,
    });
    if(result && result.resumeId) {
      const paramObj = {
        jobNoticeSeq: jobNotice.jobNoticeSeq,
        resumeId: result.resumeId,
      };
      const isSuccess = await createApplicant(paramObj);
      if (isSuccess) {
        retrieveList();
      }
    }
  }

  const createApplicant = async (paramObj) => {
    if(paramObj && paramObj.resumeId && paramObj.jobNoticeSeq) {
      try {
        const res = await mypageJobFairApplicantService.createApplicant(paramObj);
        if(res.data.code.toLowerCase() === 'success') {
          await alert(["입사지원을 하였습니다."]);
          return true;
        }
        alert(["입사지원을 실패하였습니다."]);
        return false;
      } catch (res) {
        alert(["입사지원을 실패하였습니다."]);
        return false;
      }
    }
    return false;
  }

  const emit = defineEmits(['go-back']);
  const goBack = () => {
    emit('go-back');
  }

  watch(() => props.corpId, (newCorpId) => {
    if (newCorpId) {
      retrieveList(newCorpId);
      retrieveCorpDetail(newCorpId);
    } else {
      list.value = [];
      totalCount.value = 0;
      totalPages.value = 1;
      currentPage.value = 1;
    }
  }, { immediate: true });
</script>

<template>
 <div class="table-form">
    <div class="list-top mb-n-xs">
        <KSASecondaryButton text="이전" @click="goBack()" />
    </div>
    <div class="list-top">
      <p>총 <span>{{ $number(totalCount)}}</span>개</p>
      <div class="flex-wrap">
        <KSAPrimaryButton text="기업정보 보기" @click="onClickCorp()" />&nbsp;
        <KSASelect :options="pageSizeGroup" class="select" @selectOption="onSelectPageSize"/>
      </div>
    </div>
    <div class="table-form">
      <table class="table-form board-mobile-vertical">
        <tr>
          <th>번호</th>
          <th>채용상태</th>
          <th>채용공고 제목</th>
          <th>관심 카테고리</th>
          <th>면접 방식</th>
          <th>채용기간</th>
          <th>채용공고 등록일</th>
          <th>입사지원</th>
        </tr>
        <tr v-for="jobNotice in list">
          <td>{{ jobNotice.boardNo }}</td>
          <td>{{ $gbNameKR('JOB_NOTICE_STATE_GB', jobNotice.jobNoticeStateGb) }}</td>
          <td class="clickable blue text-overflow long-overflow" @click="onClickJobNotice(jobNotice)">{{ jobNotice.jobName }}</td>
          <td>{{ jobNotice.interests }}</td>
          <td>{{ $gbNameKR('INTV_GB', jobNotice.intvGb) }}</td>
          <td>{{ $yyyymmdd(jobNotice.recruitStartYmd) }} ~ {{ $yyyymmdd(jobNotice.recruitEndYmd) }}</td>
          <td>{{ $yyyymmddHHmmss(jobNotice.createDate) }}</td>
          <td>
            <div v-if="jobNotice.jobNoticeStateGb === '01'" class="gray">지원대기</div>
            <div v-else-if="jobNotice.jobNoticeStateGb === '02'">
              <span v-if="jobNotice.applyStateGb" class="gray">지원완료</span>
              <div v-else>
                <span class="clickable no-underline" @click="onClickCorpApplicant(jobNotice)">입사지원</span>
              </div>
            </div>
            <div v-else-if="jobNotice.jobNoticeStateGb === '03'" class="gray">지원마감</div>
            <div v-else>-</div>
          </td>
        </tr>
      </table>
    </div>
    <div class="result-none lecture-none" v-if="list.length === 0">
      등록된 채용공고가 없습니다.
    </div>
    <Paginate
        v-if="list.length > 0"
        v-model="currentPage"
        :page-count="totalPages"
        :click-handler="onSelectPage"
    />
  </div>
  <JobFairCorpInfoDetailModal ref="corpInfoModal"/> <!-- 기업정보 보기 -->
  <JobFairJobNoticeDetailModal ref="jobNoticeModal" /> <!-- 채용공고 보기 -->
  <JobFairCorpApplicantResumeSelectModal v-if="isResumeSelectModalOpen" ref="resumeSelectModal" @show-resume-detail="onShowResumeDetail"/>
  <JobFairResumeDetailModal v-if="isResumeDetailModalOpen" ref="resumeDetailModal" />
  <JobFairApplyResumeConfirmModal v-if="isApplyResumeConfirmModalOpen" ref="applyResumeConfirmModal" /> <!-- 회사 이력서 안내 -->
  <JobFairCorpApplicantResumeUploadFileModal v-if="isResumeUploadFileModalOpen" ref="resumeUploadFileModal" /> <!-- 회사 이력서 입사지원 -->
</template>

<style scoped>
</style>
